import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Space,
  Typography,
  Upload,
} from "antd";
import type { UploadFile } from "antd/es/upload";

import { ApiError, type VenueRow, api } from "../api/client";

const { Title, Paragraph } = Typography;

export const VenueSettingsPage = () => {
  const [venue, setVenue] = useState<VenueRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm<VenueRow>();

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const v = await api.getVenue();
      setVenue(v);
      form.setFieldsValue(v);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      await api.updateVenue(values);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  };

  const onLogoUpload = async (file: UploadFile) => {
    try {
      const fd = new FormData();
      fd.append("file", file as unknown as File);
      const r = await fetch("/api/venue/logo", {
        method: "POST",
        credentials: "same-origin",
        body: fd,
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setError(body.detail ?? `HTTP ${r.status}`);
        return false;
      }
      await refresh();
      return false; // prevent default upload behaviour
    } catch (err) {
      setError(String(err));
      return false;
    }
  };

  return (
    <Card loading={loading}>
      <Title level={2} style={{ marginBottom: 8 }}>
        Venue branding
      </Title>
      <Paragraph type="secondary">
        Used on printable settlement receipts. Leave blank to render the generic
        "Strichliste Receipt" header.
      </Paragraph>
      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 16 }}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        initialValues={venue ?? {}}
      >
        <Form.Item label="Venue name" name="venue_name">
          <Input maxLength={255} placeholder="e.g. Anchor Brewing Tap Room" />
        </Form.Item>
        <Form.Item label="Address" name="venue_address">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item label="Receipt footer" name="receipt_footer">
          <Input.TextArea
            rows={3}
            placeholder="Thanks for visiting / VAT info / opening hours"
          />
        </Form.Item>
        <Form.Item label="Logo">
          {venue?.logo_path ? (
            <Space direction="vertical">
              <img
                src={venue.logo_path}
                alt="venue logo"
                style={{
                  maxHeight: 80,
                  background: "#fff",
                  padding: 8,
                  borderRadius: 4,
                }}
              />
              <span>{venue.logo_path}</span>
            </Space>
          ) : (
            <span style={{ color: "#888" }}>No logo set</span>
          )}
        </Form.Item>
        <Form.Item>
          <Upload
            accept=".png,.svg,.jpg,.jpeg"
            beforeUpload={onLogoUpload}
            showUploadList={false}
            maxCount={1}
          >
            <Button>Upload logo (PNG / SVG / JPG, max 1MB)</Button>
          </Upload>
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={onSave}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
